import {Component, inject} from '@angular/core';
import {PrimeNgImportsModule} from '../../../../../sdk/src/prime-ng-imports';
import {IBasePageComponent} from '../../../../../sdk/src/components/ibase-page/ibase-page.component';
import {firstValueFrom, lastValueFrom} from 'rxjs';
import {ActivatedRoute} from '@angular/router';
import {JobsInstanceCRUDComponent} from '../../../components/jobs-instance-crud/jobs-instance-crud.component';
import {CpmCJobsSpecificationApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-jobs-specification.api-service';
import {CpmCJobsSpecificationApiModel} from '../../../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-jobs-specification.api-model';
import {ECpmCJobsRoleApiEnum} from '../../../../../gen/cpm-c-api-ts/src/enums/cpmc-sdk-jobs.api-enums';
import {
    CpmAEAllPluginSpecApiSchema,
    CpmAEPluginFunctionApiSchema,
    CpmAEPluginSpecApiSchema
} from '../../../../../gen/cpm-ae-api-ts/src/schemas/ae-global/cpmae-plugins.api-schemas';
import {GenericTreeComponent} from '../../../../../sdk/src/components/generic-tree/generic-tree.component';
import {TreeColDef} from '../../../../../sdk/src/components/generic-tree/tree-col-def';
import {TreeNode} from 'primeng/api';
import {JobsSpecificationCaching} from '../../../caching/jobs-specification.caching';
import {DateUtils} from '../../../../../sdk/src/utils/date.utils';
import {ActionJobsUtils} from '../../../utils/action-jobs.utils';

@Component({
    selector: 'app-ga-jobs-page',
    templateUrl: './jobs-page.component.html',
    imports: [PrimeNgImportsModule, JobsInstanceCRUDComponent, GenericTreeComponent]
})
export class GJobsPage extends IBasePageComponent {
    protected readonly JSON = JSON;

    private readonly activatedRoute = inject(ActivatedRoute);

    protected jobType!: string;
    protected specifications: CpmCJobsSpecificationApiModel[] = [];
    protected specificationsOptions: {key: string; label: string; active: boolean}[] = [];
    protected selectedSpeficiationUUID?: string;
    protected selectedSpeficiation?: CpmCJobsSpecificationApiModel;
    protected specificationColumns: TreeColDef[] = [];

    protected specificationAppDetailsItems: TreeNode<CpmAEPluginSpecApiSchema | CpmAEPluginFunctionApiSchema>[] = [];

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        const routeData = await firstValueFrom(this.activatedRoute.data);
        this.jobType = routeData['jobType'];

        this.initSpecificationColumns();

        await this.refresh();
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * Refresh
     */
    protected async refresh() {
        this.showLoading = true;
        this.showError = false;

        this.loadingStr = 'Loading specifications';
        const result = await lastValueFrom(
            CpmCJobsSpecificationApiService.getJobsSpecificationList(
                {
                    job_type: this.jobType,
                    job_role: ECpmCJobsRoleApiEnum.EXECUTOR
                },
                CpmCJobsSpecificationApiService.dPaginatorConfig
            )
        );

        this.specifications = result.data;

        this.specificationsOptions = this.specifications.map((x) => ({
            key: x.uuid,
            label: x.hash + (x.active ? ` (Active) ` : ' ') + DateUtils.format(x.created_at),
            active: x.active
        }));

        this.showLoading = false;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On selected specification changed
     */
    protected async onSelectedSpecificationChanged() {
        this.showLoading = true;
        this.showError = false;

        await this.asyncCritical(async () => {
            this.loadingStr = 'Loading specification...';
            this.selectedSpeficiation = await firstValueFrom(
                CpmCJobsSpecificationApiService.getJobsSpecificationByUUID(this.selectedSpeficiationUUID!)
            );

            const specificationAppDetails = (await firstValueFrom(
                JobsSpecificationCaching.getExecutorActiveSpecificationAppDetails(this.jobType)
            )) as unknown as CpmAEAllPluginSpecApiSchema;

            this.specificationAppDetailsItems = [];
            specificationAppDetails.plugins.forEach((plugin) => {
                const newPluginNode: TreeNode<CpmAEPluginSpecApiSchema | CpmAEPluginFunctionApiSchema> = {
                    key: plugin.id,
                    label: plugin.name,
                    data: plugin,
                    children: [],
                    style: {fontWeight: 'bold'},
                    icon: ActionJobsUtils.ICON_PLUGIN
                };

                plugin.functions.forEach((pluginFunction) => {
                    newPluginNode.children?.push({
                        key: pluginFunction.id,
                        label: pluginFunction.name,
                        data: pluginFunction,
                        icon: ActionJobsUtils.ICON_PLUGIN_FUNCTION
                    });
                });

                this.specificationAppDetailsItems.push(newPluginNode);
            });
        });

        this.showLoading = false;
    }
    /**
     * On set active specification
     */
    protected async setActiveSpecification() {
        this.showLoading = true;
        this.showError = false;

        await this.asyncCritical(async () => {
            this.loadingStr = 'Updating active specification...';
            await firstValueFrom(
                CpmCJobsSpecificationApiService.updateExecutorActiveSpecification(this.jobType, {
                    specification_uuid: this.selectedSpeficiationUUID!
                })
            );
        });

        await this.refresh();
        await this.onSelectedSpecificationChanged();
        this.showLoading = false;
    }

    // ===============================================================
    // ===============================================================

    private initSpecificationColumns() {
        this.specificationColumns = [
            {
                field: 'id',
                header: 'ID',
                searcheable: true,
                filterable: false,
                nodeIconSide: 'left'
            },
            {
                field: 'name',
                header: 'Name',
                searcheable: true,
                filterable: false
            }
        ];
    }
}
